import { FusionDropDownInput } from 'components';
import { useEffect } from 'react';
import DeviceGroupsSelect from './DeviceGroupsSelect';

import { usePerms } from 'contexts/PermsContext';
import {
  selectDeviceTypeMap,
  selectIntegrations,
  selectSystemCount,
} from 'features/integrations/state/selectors/integrationSelectors';
import { useSelector } from 'react-redux';
import { useGetAllAvailableDeviceTypesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';
import { hasDevicePermission } from '../../../utils/devicePermissionUtils';
import { renderField } from '../utils/modalDropdownUtils';

const ModalDropdowns = ({
  deviceTypes,
  formData,
  setFormData,
  setDeviceTypes,
  device,
  layoutConfig,
}) => {
  const perms = usePerms();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const { data: integrationDeviceTypes } = useGetAllAvailableDeviceTypesQuery();
  const systemCount = useSelector(selectSystemCount);

  // Build structured device types data from context
  useEffect(() => {
    // Ensure we have the context and a deviceTypeMap to work with.
    if (!deviceTypeMap || !integrations) return;
    const grouped = {};

    // Iterate over the list of integrations (systems) the UI shows.
    // For each integration, collect all device_type ids that belong
    // to that integration's system_type from `context.deviceTypeMap`.
    (integrations || []).forEach((integration) => {
      const system = integration.system_type;

      //Get the device types for this system from deviceTypeMap
      const deviceTypesForIntegration = deviceTypeMap
        .map((deviceType) => {
          if (deviceType.system_type === system) {
            return deviceType.device_type;
          }
        })
        .filter((dt) => dt !== undefined && dt !== null);

      // If user lacks create/edit permission for all device types for this system
      const hasAnyCreateEdit = deviceTypesForIntegration.some((dt) =>
        hasDevicePermission(perms, system, dt, 'createEdit'),
      );
      if (!hasAnyCreateEdit) return;

      // Initialize the grouped bucket for this system if needed, then
      // transform the device_type ids into objects that include the
      // `validDevices` list (safely reading from context.deviceTypes).
      if (!grouped[system]) grouped[system] = [];
      grouped[system] = deviceTypesForIntegration.map((dt) => ({
        type: dt,
        validDevices: integrationDeviceTypes?.[system]?.[dt]?.validDevices || [],
      }));
    });

    // Convert the grouped map into the array shape the component expects:
    // [ { system_type: '...', device_types: [ { type, validDevices }, ... ] }, ... ]
    const newDeviceTypes = Object.keys(grouped).map((system) => ({
      system_type: system,
      device_types: grouped[system],
    }));

    // Update state so dropdowns and layout lookups can use the structured data.
    setDeviceTypes(newDeviceTypes);
  }, [perms, deviceTypeMap, integrations, integrationDeviceTypes]);

  useEffect(() => {
    if (!device || !deviceTypes.length) return;

    setFormData((prev) => {
      if (Number(prev.system_type) >= 0 && Number(prev.device_type) >= 0) {
        return prev; // don't overwrite if user already picked
      }

      const sysIdx = deviceTypes.findIndex((sys) => sys.system_type === device.system_type);

      const typeIdx =
        sysIdx >= 0
          ? deviceTypes[sysIdx].device_types.findIndex((t) => t.type === device.device_type)
          : -1;

      return {
        ...prev,
        system_type: sysIdx.toString(),
        device_type: typeIdx.toString(),
      };
    });
  }, [device, deviceTypes]);

  return (
    <div>
      <div className="d-flex flex-column gap-3">
        <FusionDropDownInput
          label="System"
          disabled={!!device}
          options={
            deviceTypes?.map((dt) => {
              // Defensive check: ensure there is at least one create/edit permission
              const hasAnyCreateEdit = (dt.device_types || []).some((d) =>
                hasDevicePermission(perms, dt.system_type, d.type || d, 'createEdit'),
              );
              if (!hasAnyCreateEdit) return null;
              const match = integrations?.find((i) => i.system_type === dt.system_type);
              return match?.system_name || dt.system_type;
            }) || []
          }
          value={formData?.system_type || -1}
          onChange={(value) => {
            return setFormData({
              ...formData,
              system_type: value,
              device_type: -1,
              selected_device: -1,
              device: null,
            });
          }}
        />
        {formData?.system_type >= 0 && (
          <>
            <FusionDropDownInput
              label="Device Type"
              disabled={!!device}
              options={(() => {
                const selectedIntegration = integrations?.[Number(formData.system_type)];

                if (!selectedIntegration) return [];

                // Find matching system in deviceTypes using system_type id
                const sysIdx = deviceTypes.findIndex(
                  (sys) => sys.system_type === selectedIntegration.system_type,
                );

                if (sysIdx < 0) return [];

                return deviceTypes[sysIdx].device_types.map((d) => d.type.replace('_', ' '));
              })()}
              value={formData?.device_type || -1}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  device_type: value,
                  selected_device: -1,
                  device: null,
                })
              }
            />
          </>
        )}

        {formData?.system_type >= 0 &&
          formData?.device_type >= 0 &&
          layoutConfig &&
          layoutConfig.components.map((field, index) => {
            return (
              <div key={field['data-field'] || field.label || index}>
                {renderField(formData, setFormData, field, deviceTypes, device)}
              </div>
            );
          })}

        <div className="fw-bold fs-6">
          <p className="mb-0">Available Licenses:</p>
          <div>
            {`${integrations?.length} of ${systemCount?.advanced_system} (Licenses remaining)`}
          </div>
        </div>
      </div>

      {formData?.device_type >= 0 && (
        <DeviceGroupsSelect
          deviceTypes={deviceTypes}
          formData={formData}
          setFormData={setFormData}
          deviceGroups={deviceGroups}
        />
      )}
    </div>
  );
};

export default ModalDropdowns;
